import { bytes32Schema, nonzeroAddressSchema, type Address, type Hex, type PreparedCall } from '@hoodmorning/contracts';
import { encodeFunctionData, getAddress, isAddressEqual, keccak256, parseAbi, type PublicClient } from 'viem';
import type { OwnerCreatorClaimContext } from './ownerOperationEvidence.js';
import { hashPreparedCalls } from './calls.js';
import { verifyContractCode } from './codeVerification.js';

export const creatorClaimAbi = parseAbi([
  'function factory() view returns (address)',
  'function token() view returns (address)',
  'function quote() view returns (address)',
  'function policyHash() view returns (bytes32)',
  'function recipients(uint256 slot) view returns (address)',
  'function creatorClaimable(uint256 slot) view returns (uint256)',
  'function claimCreator(uint256 slot) returns (uint256 amount)',
]);
const registryAbi = parseAbi(['function isLaunchCurve(address curve) view returns (bool)', 'function curveForToken(address token) view returns (address)']);

/** Claims are permissionless triggers; payment goes to the CURRENT recorded slot recipient,
 * not the caller. This read-only preparation never adds a caller-selected payout destination.
 * Amount/recipient are snapshot previews, not on-chain minimum-payout or destination guards.
 */
export async function prepareCanonicalCreatorClaim(input: {
  client: Pick<PublicClient, 'getChainId' | 'getBlock' | 'getBytecode' | 'readContract'>;
  factory: { address: Address; codeHash: Hex };
  curve: Address;
  baseToken: Address;
  quoteToken: Address;
  policyHash: Hex;
  slot: number;
  expectedRecipient: Address;
}) {
  // Snapshot caller-owned values before any asynchronous reads.
  const { client, slot } = input;
  const factory = { ...input.factory };
  const { curve, baseToken, quoteToken, policyHash, expectedRecipient } = input;
  for (const address of [factory.address, curve, baseToken, quoteToken, expectedRecipient]) nonzeroAddressSchema.parse(address);
  bytes32Schema.parse(factory.codeHash);
  bytes32Schema.parse(policyHash);
  if (!Number.isInteger(slot) || slot < 0 || slot > 4) throw new Error('Invalid creator slot');
  if (await client.getChainId() !== 4663) throw new Error('RPC chain mismatch');
  const block = await client.getBlock();
  if (block.number === null || block.hash === null) throw new Error('Claim source block unavailable');
  const blockNumber = block.number;
  const report = await verifyContractCode({ getBytecode: args => client.getBytecode({ address: args.address, blockNumber }) }, [{ name: 'launchFactory', address: factory.address, expectedCodeHash: factory.codeHash }]);
  if (!report.valid) throw new Error('Factory code verification failed');
  const [registered, tokenCurve, curveFactory, actualBase, actualQuote, actualPolicy, recipient, amount] = await Promise.all([
    client.readContract({ address: factory.address, abi: registryAbi, functionName: 'isLaunchCurve', args: [curve], blockNumber }),
    client.readContract({ address: factory.address, abi: registryAbi, functionName: 'curveForToken', args: [baseToken], blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'factory', blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'token', blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'quote', blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'policyHash', blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'recipients', args: [BigInt(slot)], blockNumber }),
    client.readContract({ address: curve, abi: creatorClaimAbi, functionName: 'creatorClaimable', args: [BigInt(slot)], blockNumber }),
  ]);
  if (!registered || !isAddressEqual(tokenCurve, curve) || !isAddressEqual(curveFactory, factory.address)) throw new Error('Claim source is not canonical');
  if (!isAddressEqual(actualBase, baseToken) || !isAddressEqual(actualQuote, quoteToken) || actualPolicy.toLowerCase() !== policyHash.toLowerCase()) throw new Error('Claim asset or policy mismatch');
  if (!isAddressEqual(recipient, expectedRecipient)) throw new Error('Creator recipient changed; refresh earnings');
  if (amount === 0n) throw new Error('Nothing to claim');
  // Keep the pinned historical reads bounded for providers that serialize
  // expensive archive lookups internally. A failure stops before later reads.
  const code: Array<Hex | undefined> = [];
  for (const address of [curve, baseToken, quoteToken]) {
    code.push(await client.getBytecode({ address, blockNumber }));
  }
  if (code.some(value => !value || value === '0x')) throw new Error('Claim instance code unavailable');
  const creatorClaim: OwnerCreatorClaimContext = {
    kind: 'creator-claim-v1', factory: getAddress(factory.address), factoryCodeHash: factory.codeHash,
    curve: getAddress(curve), curveCodeHash: keccak256(code[0]!), baseToken: getAddress(baseToken),
    baseTokenCodeHash: keccak256(code[1]!), quoteToken: getAddress(quoteToken), quoteTokenCodeHash: keccak256(code[2]!),
    policyHash, slot, preparedRecipient: getAddress(recipient), sourceBlockNumber: blockNumber.toString(), sourceBlockHash: block.hash,
  };
  if ((await client.getBlock({ blockNumber })).hash !== block.hash || await client.getChainId() !== 4663) throw new Error('Claim source changed during preparation');
  const calls: PreparedCall[] = [{ to: getAddress(curve), value: 0n, data: encodeFunctionData({ abi: creatorClaimAbi, functionName: 'claimCreator', args: [BigInt(slot)] }) }];
  return { calls, callsHash: hashPreparedCalls(calls), slot, recipient: getAddress(recipient), quoteToken: getAddress(quoteToken), creatorClaim,
    previewAmount: amount, sourceBlockNumber: blockNumber, sourceBlockHash: block.hash,
    payoutSemantics: 'current-recorded-recipient-at-execution' as const, simulationStatus: 'required' as const };
}
